/**
 * OTPInput - 6-digit code entry with auto-focus, paste, and onComplete.
 * MODULE: core/auth/frontend/components/OTPInput.tsx
 */
import { useRef, useCallback, KeyboardEvent, ChangeEvent } from 'react';

export interface OTPInputProps {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  disabled?: boolean;
  error?: boolean;
}

export function OTPInput({
  length = 6,
  value,
  onChange,
  onComplete,
  disabled = false,
  error = false,
}: OTPInputProps) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = value.replace(/\D/g, '').slice(0, length).split('');
  while (digits.length < length) digits.push('');

  const setDigit = useCallback(
    (index: number, char: string) => {
      const next = value.replace(/\D/g, '').slice(0, length).split('');
      next[index] = char;
      const joined = next.join('');
      onChange(joined);
      if (joined.length === length) onComplete?.(joined);
    },
    [value, length, onChange, onComplete]
  );

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
      setDigit(index - 1, '');
    }
  };

  const handleChange = (index: number, e: ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value.replace(/\D/g, '');
    if (v.length > 1) {
      const pasted = v.slice(0, length);
      pasted.split('').forEach((c, i) => setDigit(i, c));
      if (pasted.length === length) {
        inputRefs.current[length - 1]?.focus();
        onComplete?.(pasted);
      } else {
        inputRefs.current[pasted.length]?.focus();
      }
      return;
    }
    setDigit(index, v);
    if (v) inputRefs.current[Math.min(index + 1, length - 1)]?.focus();
  };

  return (
    <div className="flex gap-2 justify-center" role="group" aria-label="Verification code">
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => { inputRefs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={6}
          value={d}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          disabled={disabled}
          className={`w-12 h-12 text-center text-xl font-semibold rounded border ${
            error ? 'border-red-500' : 'border-gray-300'
          } focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100`}
          aria-label={`Digit ${i + 1}`}
        />
      ))}
    </div>
  );
}
